import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';
import type { ApiResponse, ApiError } from '../types';
import config from '../config';

const ACCESS_TOKEN_KEY = 'tensormaps-token';
const REFRESH_TOKEN_KEY = 'tensormaps-refresh-token';

// Axios request configs we tag with a one-shot retry flag after a transparent refresh.
type RetryableConfig = AxiosRequestConfig & { _retry?: boolean };

class ApiClient {
  private client: AxiosInstance;
  private abortControllers: Map<string, AbortController> = new Map();
  // De-duplicates concurrent refreshes: many in-flight 401s share one refresh call.
  private refreshPromise: Promise<string | null> | null = null;

  private normalizeResponse<T>(payload: unknown): ApiResponse<T> {
    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
      return {
        status: 'success',
        data: payload as T,
      };
    }

    const record = payload as Record<string, unknown>;

    if ('data' in record) {
      return {
        status: typeof record.status === 'string' ? (record.status as 'success' | 'error') : 'success',
        // The `data` key is present: return it verbatim, even when it is a
        // legitimate falsy value (null/0/''/false). Falling back to the whole
        // wrapper on falsy `data` would leak the {status,data,...} envelope.
        data: record.data as T,
        message: typeof record.message === 'string' ? record.message : undefined,
        meta: record.meta as ApiResponse<T>['meta'],
      };
    }

    return {
      status: 'success',
      data: payload as T,
    };
  }

  constructor() {
    this.client = axios.create({
      baseURL: config.api.baseUrl,
      timeout: config.api.timeout,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor
    this.client.interceptors.request.use(
      (config) => {
        // Add auth token if available
        const token = localStorage.getItem('tensormaps-token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor: on a 401, attempt a single transparent token refresh
    // and retry the original request. Anonymous traffic (no refresh token) is left
    // untouched so unauthenticated requests still go out and surface their errors.
    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError<ApiError>) => {
        const original = error.config as RetryableConfig | undefined;
        const status = error.response?.status;
        const url = original?.url ?? '';
        const isAuthEndpoint =
          url.includes('/auth/login') ||
          url.includes('/auth/register') ||
          url.includes('/auth/refresh');
        const hasRefreshToken = !!this.getRefreshToken();

        if (status === 401 && original && !original._retry && !isAuthEndpoint && hasRefreshToken) {
          original._retry = true;
          const newToken = await this.getValidRefreshedToken();
          if (newToken) {
            original.headers = { ...(original.headers || {}), Authorization: `Bearer ${newToken}` };
            return this.client(original);
          }
          // Refresh failed: clear tokens and ask the app to surface the login modal.
          this.clearTokens();
          window.dispatchEvent(new CustomEvent('auth:session-expired'));
        }

        return Promise.reject(this.normalizeError(error));
      }
    );
  }

  private getRefreshToken(): string | null {
    try {
      return localStorage.getItem(REFRESH_TOKEN_KEY);
    } catch {
      return null;
    }
  }

  private clearTokens(): void {
    try {
      localStorage.removeItem(ACCESS_TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
    } catch {
      /* ignore */
    }
  }

  // Performs (or joins an in-flight) refresh and returns the new access token.
  private getValidRefreshedToken(): Promise<string | null> {
    if (!this.refreshPromise) {
      this.refreshPromise = this.performRefresh().finally(() => {
        this.refreshPromise = null;
      });
    }
    return this.refreshPromise;
  }

  private async performRefresh(): Promise<string | null> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) return null;
    try {
      // Bare axios call (not `this.client`) so it bypasses the interceptors and
      // cannot recurse into another refresh attempt.
      const resp = await axios.post(
        `${config.api.baseUrl}/auth/refresh`,
        { refresh_token: refreshToken },
        { headers: { 'Content-Type': 'application/json' }, timeout: config.api.timeout }
      );
      // /auth/refresh returns `{ status, tokens: { access_token, refresh_token, ... } }`.
      const body = (resp.data?.data ?? resp.data) as {
        tokens?: { access_token?: string; refresh_token?: string };
      };
      const tokens = body?.tokens;
      const access = tokens?.access_token;
      const refresh = tokens?.refresh_token;
      if (!access) return null;
      try {
        localStorage.setItem(ACCESS_TOKEN_KEY, access);
        if (refresh) localStorage.setItem(REFRESH_TOKEN_KEY, refresh);
      } catch {
        /* ignore */
      }
      // Let the auth store resync its in-memory token copies.
      window.dispatchEvent(
        new CustomEvent('auth:token-refreshed', {
          detail: { access_token: access, refresh_token: refresh },
        })
      );
      return access;
    } catch {
      return null;
    }
  }

  private normalizeError(error: AxiosError<ApiError>): ApiError {
    if (error.response?.data) {
      return error.response.data;
    }
    if (error.code === 'ECONNABORTED') {
      return { code: 'TIMEOUT', message: 'Request timed out' };
    }
    if (!navigator.onLine) {
      return { code: 'OFFLINE', message: 'No internet connection' };
    }
    return { code: 'UNKNOWN', message: error.message || 'An error occurred' };
  }

  private createAbortController(key: string): AbortController {
    // Cancel any existing request with the same key
    this.cancelRequest(key);
    const controller = new AbortController();
    this.abortControllers.set(key, controller);
    return controller;
  }

  cancelRequest(key: string): void {
    const controller = this.abortControllers.get(key);
    if (controller) {
      controller.abort();
      this.abortControllers.delete(key);
    }
  }

  async get<T>(
    url: string,
    config?: AxiosRequestConfig & { abortKey?: string }
  ): Promise<ApiResponse<T>> {
    const { abortKey, ...axiosConfig } = config || {};
    const signal = abortKey
      ? this.createAbortController(abortKey).signal
      : undefined;

    const response = await this.client.get<ApiResponse<T>>(url, {
      ...axiosConfig,
      signal,
    });
    return this.normalizeResponse<T>(response.data);
  }

  async post<T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    const response = await this.client.post<ApiResponse<T>>(url, data, config);
    return this.normalizeResponse<T>(response.data);
  }

  async put<T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    const response = await this.client.put<ApiResponse<T>>(url, data, config);
    return this.normalizeResponse<T>(response.data);
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.client.delete<ApiResponse<T>>(url, config);
    return this.normalizeResponse<T>(response.data);
  }
}

export const api = new ApiClient();
